import { Construction, Database } from 'lucide-react';

import { Tooltip } from '@/components/ui/tooltip';
import type { Device } from '@/entities/device';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useMe } from '@/hooks/use-me';
import { FeatureFlag } from '@/types/feature-flag';

import {
  PageActionItem,
  PageActionItemButton,
  PageActionItemDescription,
} from './page-actions';

export type PageActionDataExplorerProps = {
  device: Device | null;
};
export function PageActionDataExplorer({
  device,
}: PageActionDataExplorerProps) {
  const featureFlag = useFeatureFlag();
  const { asset } = useCurrentAsset();
  const me = useMe();

  // Check if either dataExplorer or newDataExplorer is enabled
  if (
    !asset ||
    !(featureFlag('dataExplorer') || featureFlag('newDataExplorer')) ||
    !device
  ) {
    return null;
  }

  // Check if the feature is internal-only
  const isDataExplorerInternal =
    me.config.featureFlags.dataExplorer === FeatureFlag.INTERNAL ||
    me.config.featureFlags.newDataExplorer === FeatureFlag.INTERNAL ||
    me.config.featureFlags.detailsToDexLink === FeatureFlag.INTERNAL;

  // Only show internal indicator for internal users (not for customers)
  const showInternalIndicator =
    isDataExplorerInternal && me.isInternal && me.config.id !== '49';

  return (
    <PageActionItem>
      <PageActionItemDescription>
        {showInternalIndicator && (
          <Tooltip
            delay
            position="top"
            content={<p>INTERNAL - not yet available for {me.tenant}</p>}
          >
            <Construction className="size-4 text-yellow-600" />
          </Tooltip>
        )}
        {showInternalIndicator && <span className="mr-1" />}
        Explore the data in the{' '}
        <span className="font-semibold">Data Explorer</span>
      </PageActionItemDescription>
      <div className="relative">
        <PageActionItemButton
          data-testid="page-action-data-explorer-button"
          to={{
            pathname: `/asset/${asset.id}/data-explorer`,
            search: new URLSearchParams({
              device: device.id,
            }).toString(),
          }}
          icon={Database}
        >
          Data Explorer
        </PageActionItemButton>
      </div>
    </PageActionItem>
  );
}
